import React, { useContext, useEffect, useState } from "react";
import Header from "../components/Header/Header";
import Footer from "../components/shared/Footer";
import { Helmet } from "react-helmet-async";
import { scrollToTop } from "../common/Helper";
import { useGetProductByIdQuery } from "../services/ProductServices";
import { useLocation, useParams } from "react-router-dom";
import { StyleContext } from "../context/StyleContextProvider";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Rating,
  TextField,
  Typography,
  useMediaQuery,
  Alert,
  Select,
  MenuItem,
} from "@mui/material";
import { Wallpaper } from "../components/shared/Wallpaper";
import { ProductImages } from "../components/Product/ProductImages";
import { StarOutline } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { getProfile } from "../reduxenv/Slices/ProfileSlice";
import { AppColors } from "../common/AppColors";
import useErrorPage from "../hooks/useErrorPage";

interface ProductOption {
  id: number;
  category: string; // CPU, MEMORY, STORAGE, NIC, WARRANTY
  name: string;
  price: number;
  minQty?: number;
  maxQty?: number;
}

interface ProductImage {
  url: string;
  [key: string]: any;
}

interface ProductModel {
  id: number;
  name: string;
  price: number;
  sku?: string;
  averageRating?: number;
  shortDescription?: string;
  description?: string;
  images?: ProductImage[];
  options?: ProductOption[];
  type?: string;
}

// Hard-coded Product Details
const HARD_CODED_PRODUCT: ProductModel = {
  id: 1,
  name: "Acer PowerEdge Server",
  price: 15999.99,
  sku: "ACR-PE-2024-001",
  averageRating: 4.5,
  shortDescription:
    "Enterprise-grade server with high performance and reliability",
  description:
    "<p>The Acer PowerEdge Server is a cutting-edge solution designed for modern data centers and business-critical applications. With support for the latest processors and expandable memory, this server provides the foundation for your enterprise infrastructure.</p><p><strong>Key Features:</strong></p><ul><li>Dual-socket architecture supporting up to 48 cores</li><li>Maximum 3TB DDR5 memory expansion</li><li>Multiple drive bays for hybrid storage configurations</li><li>Integrated RAID support for data protection</li><li>Redundant power supplies for high availability</li><li>Advanced thermal management system</li></ul>",
  images: [
    {
      url: "https://via.placeholder.com/400x400?text=Acer+PowerEdge+Front",
    },
    {
      url: "https://via.placeholder.com/400x400?text=Acer+PowerEdge+Side",
    },
  ],
  options: [
    // CPU Options
    {
      id: 101,
      category: "CPU",
      name: "Intel Xeon Silver 4410Y (24-Core)",
      price: 1500,
    },
    {
      id: 102,
      category: "CPU",
      name: "Intel Xeon Gold 6430 (32-Core)",
      price: 3500,
    },
    {
      id: 103,
      category: "CPU",
      name: "Intel Xeon Platinum 8490 (60-Core)",
      price: 7200,
    },

    // Memory Options
    {
      id: 201,
      category: "MEMORY",
      name: "32GB DDR5 RDIMM",
      price: 800,
    },
    {
      id: 202,
      category: "MEMORY",
      name: "64GB DDR5 RDIMM",
      price: 1600,
    },
    {
      id: 203,
      category: "MEMORY",
      name: "128GB DDR5 RDIMM",
      price: 3200,
    },

    // Storage Options
    {
      id: 301,
      category: "STORAGE",
      name: "480GB SSD SATA",
      price: 150,
    },
    {
      id: 302,
      category: "STORAGE",
      name: "960GB SSD NVMe",
      price: 400,
    },
    {
      id: 303,
      category: "STORAGE",
      name: "1.92TB SSD NVMe",
      price: 750,
    },
    {
      id: 304,
      category: "STORAGE",
      name: "3.84TB SSD NVMe",
      price: 1400,
    },

    // RAID Controller Options
    {
      id: 401,
      category: "RAID",
      name: "PERC H745P with 4GB Cache",
      price: 500,
    },
    {
      id: 402,
      category: "RAID",
      name: "PERC H755 with 8GB Cache",
      price: 1200,
    },

    // Network Interface Options
    {
      id: 501,
      category: "NIC",
      name: "Dual 1GbE Network Card",
      price: 0,
    },
    {
      id: 502,
      category: "NIC",
      name: "Dual 10GbE Network Card",
      price: 300,
    },
    {
      id: 503,
      category: "NIC",
      name: "Quad 10GbE Network Card",
      price: 600,
    },

    // Warranty Options
    {
      id: 601,
      category: "WARRANTY",
      name: "1 Year Hardware Support",
      price: 0,
    },
    {
      id: 602,
      category: "WARRANTY",
      name: "3 Year ProSupport Plus",
      price: 2000,
    },
    {
      id: 603,
      category: "WARRANTY",
      name: "5 Year ProSupport Enterprise",
      price: 4500,
    },
  ],
};

const AcerQuotationForm: React.FC = () => {
  const isMobile = useMediaQuery("(max-width: 786px)");
  const styleContext = useContext(StyleContext);
  const [styles, setStyles] = useState(
    styleContext.getComponentStyle("productPage"),
  );
  const location = useLocation();
  const { id } = useParams();
  const productId = Number(id);
  const { data: productResponse, isFetching: isFetchingProduct } =
    useGetProductByIdQuery(
      { productId: productId! },
      { skip: !!location.state },
    );

  const [product, setProduct] = useState<ProductModel>(
    location.state || HARD_CODED_PRODUCT,
  );
  const profile = useSelector(getProfile);

  const [processor, setProcessor] = useState<number | "">("");
  const [memory, setMemory] = useState<number | "">("");
  const [memoryQty, setMemoryQty] = useState<number>(2);

  const [storage, setStorage] = useState<number | "">("");
  const [storageQty, setStorageQty] = useState<number>(1);

  const [raidController, setRaidController] = useState<number | "">("");
  const [nic, setNic] = useState<number | "">("");
  const [warranty, setWarranty] = useState<number | "">("");

  // Quotation Form States
  const [quantity, setQuantity] = useState<number>(1);
  const [comments, setComments] = useState<string>("");
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [toErrorPage] = useErrorPage();

  useEffect(() => {
    setStyles(styleContext.getComponentStyle("productPage"));
    scrollToTop();
  }, [isMobile]);

  useEffect(() => {
    if (productResponse?.response) {
      setProduct(productResponse.response);
    } else if (!location.state) {
      // Use hard-coded product if no API response and no location state
      setProduct(HARD_CODED_PRODUCT);
    }
  }, [productResponse, location.state]);

  const getOptions = (category: string) =>
    product?.options?.filter(
      (o) => o.category.toUpperCase() === category.toUpperCase(),
    ) || [];

  const getOptionPrice = (id: number | "") => {
    if (!id) return 0;

    return product?.options?.find((o) => o.id === id)?.price || 0;
  };

  const getOptionName = (id: number | "") => {
    if (!id) return "";

    return product?.options?.find((o) => o.id === id)?.name || "";
  };

  const estimatedPrice =
    (product?.price || 0) +
    getOptionPrice(processor) +
    getOptionPrice(memory) * memoryQty +
    getOptionPrice(storage) * storageQty +
    getOptionPrice(raidController) +
    getOptionPrice(nic) +
    getOptionPrice(warranty);

  const totalPrice = estimatedPrice * quantity;

  const handleSubmitQuotation = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!quantity || quantity < 1) {
      alert("Please enter a valid quantity");
      return;
    }

    if (!processor) {
      alert("Please select a processor");
      return;
    }

    if (!memory) {
      alert("Please select memory");
      return;
    }

    if (!storage) {
      alert("Please select storage");
      return;
    }

    // Here you would typically send this data to your backend
    console.log({
      productId: product?.id,
      quantity,
      processor,
      processorName: getOptionName(processor),
      memory,
      memoryQty,
      storage,
      storageQty,
      raidController,
      nic,
      warranty,
      comments,
      estimatedPrice: totalPrice,
    });

    // Show success message
    setSubmitSuccess(true);

    // Reset form
    setQuantity(1);
    setProcessor("");
    setMemory("");
    setMemoryQty(2);
    setStorage("");
    setStorageQty(1);
    setRaidController("");
    setNic("");
    setWarranty("");
    setComments("");

    // Hide success message after 3 seconds
    setTimeout(() => {
      setSubmitSuccess(false);
    }, 3000);
  };

  return (
    <>
      <Helmet>
        <title>{product?.name || "Quotation Request"}</title>
      </Helmet>
      <Header />
      <Grid container justifyContent={"center"}>
        <Wallpaper
          content={
            <Box sx={styles.wallpaperContent}>
              <Grid container alignItems={"center"} justifyContent={"unset"}>
                <Grid item xs={12} md={12}>
                  <Typography variant="h1" sx={styles.productName}>
                    {product?.name || "Product Quotation"}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          }
        />
        <Container maxWidth={"lg"} sx={{ py: 4 }}>
          {submitSuccess && (
            <Alert severity="success" sx={{ mb: 3 }}>
              Quotation request submitted successfully! We'll contact you soon.
            </Alert>
          )}

          <Grid container spacing={4}>
            {/* Product Details Section */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    Product Details
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  {/* Product Image */}
                  {product?.images && product.images.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <ProductImages
                        imageUrls={product.images.map((img) => img.url)}
                      />
                    </Box>
                  )}

                  {/* SKU and Rating */}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        SKU
                      </Typography>
                      <Typography variant="body1">
                        {product?.sku || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Rating
                      </Typography>
                      <Rating
                        value={
                          product?.averageRating
                            ? Number(product.averageRating)
                            : 0
                        }
                        emptyIcon={
                          <StarOutline sx={styles.noRating} fontSize="small" />
                        }
                        readOnly
                        size="small"
                      />
                    </Grid>
                  </Grid>

                  {/* Price */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Unit Price
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ color: AppColors.MainColor }}
                    >
                      {product?.price?.toFixed(2)}{" "}
                      {profile?.country?.currency || ""}
                    </Typography>
                  </Box>

                  {/* Description */}
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    sx={{ mb: 1 }}
                  >
                    Description
                  </Typography>
                  {product?.shortDescription && (
                    <div
                      style={{ marginBottom: "16px", lineHeight: "1.6" }}
                      dangerouslySetInnerHTML={{
                        __html: product.shortDescription,
                      }}
                    />
                  )}
                  {product?.description && (
                    <div
                      style={{ lineHeight: "1.6" }}
                      dangerouslySetInnerHTML={{
                        __html: product.description,
                      }}
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Quotation Form Section */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    Request Quotation
                  </Typography>
                  <Divider sx={{ mb: 3 }} />

                  <Box
                    component="form"
                    onSubmit={handleSubmitQuotation}
                    noValidate
                  >
                    {/* Product Info - Read Only */}
                    <TextField
                      fullWidth
                      label="Product Name"
                      value={product?.name || ""}
                      disabled
                      margin="normal"
                      variant="outlined"
                    />

                    {/* Quantity */}
                    <TextField
                      fullWidth
                      label="Quantity *"
                      type="number"
                      value={quantity}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        if (val > 0) setQuantity(val);
                      }}
                      margin="normal"
                      variant="outlined"
                      inputProps={{ min: "1", step: "1" }}
                      required
                      helperText="Enter the quantity you need"
                    />

                    {/* Processor Dropdown */}
                    <TextField
                      select
                      fullWidth
                      label="Processor *"
                      value={processor}
                      onChange={(e) => setProcessor(Number(e.target.value))}
                      margin="normal"
                      variant="outlined"
                      required
                    >
                      <MenuItem value="">-- Select Processor --</MenuItem>
                      {getOptions("CPU").map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name} (+{item.price.toFixed(2)})
                        </MenuItem>
                      ))}
                    </TextField>

                    {/* Memory Dropdown */}
                    <TextField
                      select
                      fullWidth
                      label="Memory *"
                      value={memory}
                      onChange={(e) => setMemory(Number(e.target.value))}
                      margin="normal"
                      variant="outlined"
                      required
                    >
                      <MenuItem value="">-- Select Memory --</MenuItem>
                      {getOptions("MEMORY").map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name} (+{item.price.toFixed(2)})
                        </MenuItem>
                      ))}
                    </TextField>

                    {/* Memory Quantity */}
                    <TextField
                      fullWidth
                      label="Memory Quantity"
                      type="number"
                      value={memoryQty}
                      onChange={(e) => setMemoryQty(Number(e.target.value))}
                      margin="normal"
                      variant="outlined"
                      inputProps={{
                        min: 2,
                        max: 4,
                      }}
                    />

                    {/* Storage Dropdown */}
                    <TextField
                      select
                      fullWidth
                      label="Storage *"
                      value={storage}
                      onChange={(e) => setStorage(Number(e.target.value))}
                      margin="normal"
                      variant="outlined"
                      required
                    >
                      <MenuItem value="">-- Select Storage --</MenuItem>
                      {getOptions("STORAGE").map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name} (+{item.price.toFixed(2)})
                        </MenuItem>
                      ))}
                    </TextField>

                    {/* Storage Quantity */}
                    <TextField
                      fullWidth
                      label="Storage Quantity"
                      type="number"
                      value={storageQty}
                      onChange={(e) => setStorageQty(Number(e.target.value))}
                      margin="normal"
                      variant="outlined"
                      inputProps={{
                        min: 1,
                        max: 16,
                      }}
                    />

                    {/* RAID Controller */}
                    <TextField
                      select
                      fullWidth
                      label="RAID Controller"
                      value={raidController}
                      onChange={(e) =>
                        setRaidController(Number(e.target.value))
                      }
                      margin="normal"
                      variant="outlined"
                    >
                      <MenuItem value="">-- Select RAID Controller --</MenuItem>
                      {getOptions("RAID").map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </TextField>

                    {/* Network Card */}
                    <TextField
                      select
                      fullWidth
                      label="Network Card"
                      value={nic}
                      onChange={(e) => setNic(Number(e.target.value))}
                      margin="normal"
                      variant="outlined"
                    >
                      <MenuItem value="">-- Select Network Card --</MenuItem>
                      {getOptions("NIC").map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </TextField>

                    {/* Warranty */}
                    <TextField
                      select
                      fullWidth
                      label="Warranty"
                      value={warranty}
                      onChange={(e) => setWarranty(Number(e.target.value))}
                      margin="normal"
                      variant="outlined"
                    >
                      <MenuItem value="">-- Select Warranty --</MenuItem>
                      {getOptions("WARRANTY").map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </TextField>

                    {/* Comments */}
                    <TextField
                      fullWidth
                      label="Comments"
                      value={comments}
                      onChange={(e) => setComments(e.target.value)}
                      margin="normal"
                      variant="outlined"
                      multiline
                      rows={3}
                      placeholder="Add any additional information or special requirements..."
                      helperText="Optional: Add any special requirements or comments"
                    />

                    {/* Configuration Summary */}
                    <Box
                      sx={{
                        mt: 3,
                        p: 2,
                        backgroundColor: "#f5f5f5",
                        borderRadius: 1,
                        border: "1px solid #e0e0e0",
                      }}
                    >
                      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                        Configuration Summary
                      </Typography>

                      <Grid container spacing={1}>
                        <Grid item xs={8}>
                          <Typography variant="body2">Base Server:</Typography>
                        </Grid>
                        <Grid item xs={4} sx={{ textAlign: "right" }}>
                          <Typography variant="body2" fontWeight="bold">
                            {(product?.price || 0).toFixed(2)}
                          </Typography>
                        </Grid>

                        <Grid item xs={8}>
                          <Typography variant="body2">CPU:</Typography>
                        </Grid>
                        <Grid item xs={4} sx={{ textAlign: "right" }}>
                          <Typography variant="body2" fontWeight="bold">
                            {getOptionName(processor) || "--"}
                          </Typography>
                        </Grid>

                        <Grid item xs={8}>
                          <Typography variant="body2">
                            Memory: {memoryQty} ×
                          </Typography>
                        </Grid>
                        <Grid item xs={4} sx={{ textAlign: "right" }}>
                          <Typography variant="body2" fontWeight="bold">
                            {getOptionName(memory) || "--"}
                          </Typography>
                        </Grid>

                        <Grid item xs={8}>
                          <Typography variant="body2">
                            Storage: {storageQty} ×
                          </Typography>
                        </Grid>
                        <Grid item xs={4} sx={{ textAlign: "right" }}>
                          <Typography variant="body2" fontWeight="bold">
                            {getOptionName(storage) || "--"}
                          </Typography>
                        </Grid>

                        <Grid item xs={8}>
                          <Typography variant="body2">RAID:</Typography>
                        </Grid>
                        <Grid item xs={4} sx={{ textAlign: "right" }}>
                          <Typography variant="body2" fontWeight="bold">
                            {getOptionName(raidController) || "--"}
                          </Typography>
                        </Grid>

                        <Grid item xs={8}>
                          <Typography variant="body2">NIC:</Typography>
                        </Grid>
                        <Grid item xs={4} sx={{ textAlign: "right" }}>
                          <Typography variant="body2" fontWeight="bold">
                            {getOptionName(nic) || "--"}
                          </Typography>
                        </Grid>

                        <Grid item xs={8}>
                          <Typography variant="body2">Warranty:</Typography>
                        </Grid>
                        <Grid item xs={4} sx={{ textAlign: "right" }}>
                          <Typography variant="body2" fontWeight="bold">
                            {getOptionName(warranty) || "--"}
                          </Typography>
                        </Grid>
                      </Grid>

                      <Divider sx={{ my: 2 }} />

                      <Grid container spacing={1}>
                        <Grid item xs={8}>
                          <Typography variant="h6">
                            Total Price (Qty: {quantity}):
                          </Typography>
                        </Grid>
                        <Grid item xs={4} sx={{ textAlign: "right" }}>
                          <Typography
                            variant="h6"
                            sx={{
                              color: AppColors.MainColor,
                              fontWeight: "bold",
                            }}
                          >
                            {totalPrice.toFixed(2)} {profile?.country?.currency}
                          </Typography>
                        </Grid>
                      </Grid>

                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ mt: 1, display: "block" }}
                      >
                        {profile?.country?.isTaxIncluded
                          ? "Including GST "
                          : "Excluding GST "}
                        {profile?.country?.taxPercentage || "0"}%
                      </Typography>
                    </Box>

                    {/* Submit Button */}
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      size="large"
                      type="submit"
                      sx={{ mt: 3 }}
                    >
                      Submit Quotation Request
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Footer />
    </>
  );
};

export default AcerQuotationForm;
